import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { AdminEquipmentService, BrandService, EquipmentModelService } from '@app/api/api';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'meu-modal-edit-user-equipment',
  templateUrl: './modal-edit-user-equipment.component.html',
  styleUrls: ['./modal-edit-user-equipment.component.scss'],
})
export class ModalEditUserEquipmentComponent implements OnInit {
  userId: string = '';
  equipmentId: string = '';
  saving = false;
  lstBrands: any[] = [];
  lstEquipmentModels: any[] = [];

  equipmentForm = new FormGroup({
    equipment_model_id: new FormControl('', [Validators.required]),
    brand_id: new FormControl('', [Validators.required]),
    equipment_model_prefix_id: new FormControl(null),
    name: new FormControl('', [Validators.required]),
    serial_number: new FormControl(''),
    custom_reference: new FormControl(''),
    year_of_manufacture: new FormControl(null),
    license_plate: new FormControl(''),
    hour_meter: new FormControl(null),
    location: new FormControl(''),
    brand_name_manual: new FormControl(''),
    model_name_manual: new FormControl(''),
    chassis_serial_number: new FormControl(''),
    engine_brand: new FormControl(''),
    engine_model: new FormControl(''),
    engine_serial_number: new FormControl(''),
    transmission_brand: new FormControl(''),
    transmission_model: new FormControl(''),
    transmission_serial_number: new FormControl(''),
    axle_brand: new FormControl(''),
    axle_model: new FormControl(''),
    axle_serial_number: new FormControl(''),
    mast_type: new FormControl(''),
    mast_model: new FormControl(''),
    mast_serial_number: new FormControl(''),
    // Tire information
    steering_tire_size: new FormControl(''),
    steering_tire_type: new FormControl(''),
    load_tire_size: new FormControl(''),
    load_tire_type: new FormControl(''),
    spare_tire_size: new FormControl(''),
    spare_tire_type: new FormControl(''),
    origin: new FormControl(''),
    status: new FormControl('active', [Validators.required]),
    notes: new FormControl(''),
  });

  statusOptions: string[] = ['active', 'inactive', 'maintenance', 'retired'];

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Ngừng hoạt động';
      case 'maintenance':
        return 'Bảo trì';
      case 'retired':
        return 'Ngừng sử dụng';
      default:
        return status || '';
    }
  }

  constructor(
    public dialogRef: MatDialogRef<ModalEditUserEquipmentComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { userId: string; equipment: any },
    private adminEquipmentService: AdminEquipmentService,
    private brandService: BrandService,
    private equipmentModelService: EquipmentModelService,
    private toastr: ToastrService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.userId = this.data.userId;
    const equipment = this.data.equipment || {};
    this.equipmentId = equipment.id || '';
    this.loadBrands();
    this.loadEquipmentModels();
    this.patchForm(equipment);
  }

  loadBrands() {
    this.brandService.apiBrandsGet('', '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstBrands = res.data.collection || [];
        // Ensure current brand is present in the list
        const brandCtrl = this.equipmentForm.get('brand_id');
        const currentBrandId = brandCtrl ? brandCtrl.value : '';
        if (currentBrandId) {
          const exists = this.lstBrands.some((b) => b.id === currentBrandId);
          const equipment = this.data.equipment || {};
          if (!exists && equipment.extend_brand) {
            this.lstBrands.push(equipment.extend_brand);
          }
        }
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  loadEquipmentModels() {
    this.equipmentModelService.apiEquipmentModelsGet('', '', 1, 1000).subscribe((res) => {
      if (res.success) {
        this.lstEquipmentModels = res.data.collection || [];
        const modelCtrl = this.equipmentForm.get('equipment_model_id');
        const currentModelId = modelCtrl ? modelCtrl.value : '';
        if (currentModelId) {
          const exists = this.lstEquipmentModels.some((m) => m.id === currentModelId);
          const equipment = this.data.equipment || {};
          if (!exists && equipment.extend_equipment_model) {
            this.lstEquipmentModels.push(equipment.extend_equipment_model);
          }
        }
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  patchForm(equipment: any) {
    if (!equipment) {
      return;
    }
    this.equipmentForm.patchValue({
      equipment_model_id: equipment.equipment_model_id || '',
      brand_id: equipment.brand_id || '',
      equipment_model_prefix_id: equipment.equipment_model_prefix_id || null,
      name: equipment.name || '',
      serial_number: equipment.serial_number || '',
      custom_reference: equipment.custom_reference || '',
      year_of_manufacture: equipment.year_of_manufacture != null ? equipment.year_of_manufacture : null,
      license_plate: equipment.license_plate || '',
      hour_meter: equipment.hour_meter != null ? equipment.hour_meter : null,
      location: equipment.location || '',
      brand_name_manual: equipment.brand_name_manual || '',
      model_name_manual: equipment.model_name_manual || '',
      chassis_serial_number: equipment.chassis_serial_number || '',
      engine_brand: equipment.engine_brand || '',
      engine_model: equipment.engine_model || '',
      engine_serial_number: equipment.engine_serial_number || '',
      transmission_brand: equipment.transmission_brand || '',
      transmission_model: equipment.transmission_model || '',
      transmission_serial_number: equipment.transmission_serial_number || '',
      axle_brand: equipment.axle_brand || '',
      axle_model: equipment.axle_model || '',
      axle_serial_number: equipment.axle_serial_number || '',
      mast_type: equipment.mast_type || '',
      mast_model: equipment.mast_model || '',
      mast_serial_number: equipment.mast_serial_number || '',
      // Tire information
      steering_tire_size: equipment.steering_tire_size || '',
      steering_tire_type: equipment.steering_tire_type || '',
      load_tire_size: equipment.load_tire_size || '',
      load_tire_type: equipment.load_tire_type || '',
      spare_tire_size: equipment.spare_tire_size || '',
      spare_tire_type: equipment.spare_tire_type || '',
      origin: equipment.origin || '',
      status: equipment.status || 'active',
      notes: equipment.notes || '',
    }, { emitEvent: false });
    this.changeDetectorRef.detectChanges();
  }

  hasError(controlName: string): boolean {
    const control = this.equipmentForm.get(controlName);
    return control ? control.invalid && (control.touched || control.dirty) : false;
  }

  save() {
    if (this.equipmentForm.invalid) {
      Object.keys(this.equipmentForm.controls).forEach((name) => {
        const ctrl = this.equipmentForm.get(name);
        if (ctrl) {
          ctrl.markAsTouched();
        }
      });
      return;
    }

    const payload = this.equipmentForm.value;
    // Normalize empty strings to null for numeric fields
    ['year_of_manufacture', 'hour_meter', 'equipment_model_prefix_id'].forEach((key) => {
      if (payload[key] === '' || payload[key] === undefined) {
        payload[key] = null;
      }
    });

    this.saving = true;
    this.adminEquipmentService
      .apiAdminUsersEquipmentIdPut(this.userId, this.equipmentId, payload)
      .subscribe(
        (res) => {
          this.saving = false;
          if (res.success) {
            this.dialogRef.close({ event: true });
          } else {
            this.dialogRef.close({ event: false, message: res.message });
          }
        },
        (err) => {
          this.saving = false;
          const errorObj = err || {};
          const innerErr = errorObj.error || {};
          const message = innerErr.message || errorObj.message || 'Lỗi khi cập nhật thiết bị';
          this.toastr.error(message);
        }
      );
  }

  closeModal() {
    this.dialogRef.close();
  }
}
